<script>
    import { onMount, afterUpdate } from 'svelte';
    import moment from 'moment';
    import { activeMenu } from "../stores";
    import api_handleApiCalls from "../utils/apiCallHandler.js";
    import { pError } from "../utils/notify.html";

    let isLoading = true;
    let batches = [];
    let batch = null;      // batch being browsed
    let path = [];         // folders from the batch root down to the current folder
    let folders = [];
    let files = [];
    let viewer = null;     // file open in the viewer

    const TYPES = {
        pdf: { icon: "file-text", label: "PDF", action: "Open" },
        image: { icon: "image", label: "Image", action: "View" },
        recordedLecture: { icon: "video", label: "Video Lecture", action: "Watch" },
        youtubeLecture: { icon: "youtube", label: "YouTube Lecture", action: "Watch" },
        refLink: { icon: "link", label: "Reference Link", action: "Open" },
        externalLink: { icon: "external-link", label: "External Link", action: "Open" },
    };
    // Office files are uploaded as documents (type "pdf"); tell them apart by extension
    const OFFICE_TYPES = {
        ppt: { icon: "monitor", label: "Presentation" },
        pptx: { icon: "monitor", label: "Presentation" },
        doc: { icon: "file-text", label: "Document" },
        docx: { icon: "file-text", label: "Document" },
        xls: { icon: "grid", label: "Spreadsheet" },
        xlsx: { icon: "grid", label: "Spreadsheet" },
    };
    const officeType = (file) => file.type == "pdf" ? OFFICE_TYPES[String(file.fileExtension).toLowerCase()] : null;
    const typeInfo = (file) => {
        let office = officeType(file);
        if (office) return { icon: office.icon, label: office.label, action: "Open" };
        return TYPES[file.type] || { icon: "file", label: "File", action: "Open" };
    };

    // Browsers can't render Office files, so they go through Microsoft's Office viewer,
    // which needs the file at a public URL (S3 uploads are public-read)
    const officeViewer = (file, mode) => `https://view.officeapps.live.com/op/${mode}.aspx?src=${encodeURIComponent(file.downloadUrl)}`;
    const viewerSrc = (file) => officeType(file) ? officeViewer(file, "embed") : file.downloadUrl;
    const newTabUrl = (file) => officeType(file) ? officeViewer(file, "view") : file.downloadUrl;

    async function load(batchId, folderId) {
        isLoading = true;
        let res = await api_handleApiCalls("folder.getStudentStudyMaterial", { batchId, folderId });
        isLoading = false;
        if (!res.status) return pError("Error", res.errMsg);
        batches = res.data.batches || [];
        batch = res.data.batch || null;
        path = res.data.path || [];
        folders = res.data.folders || [];
        files = res.data.files || [];
        // Skip the batch list when there is only one batch to choose from
        if (!batch && batches.length == 1) load(batches[0]._id);
    }

    function youtubeId(url) {
        let match = (url || "").match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([\w-]{11})/);
        return match ? match[1] : null;
    }

    function openFile(file) {
        let link = file.refUrl || file.link || file.url;
        if (file.type == "youtubeLecture") {
            if (youtubeId(file.youtubeLink)) viewer = file;
            else window.open(file.youtubeLink, "_blank", "noopener");
        } else if (file.type == "refLink" || file.type == "externalLink") {
            if (link) window.open(link, "_blank", "noopener");
            else pError("Not available", "This link is missing. Please contact your institute.");
        } else if (!file.downloadUrl) {
            pError("Not available", "This file can't be opened right now. Please contact your institute.");
        } else {
            viewer = file;
        }
    }

    function onKeydown(event) {
        if (event.key == "Escape") viewer = null;
    }

    onMount(() => {
        activeMenu.set("studentStudyMaterial");
        load();
    });

    afterUpdate(() => {
        feather.replace();
    });
</script>

<svelte:window on:keydown={onKeydown} />

<style>
    .sm-row {
        display: flex;
        align-items: center;
        text-align: left;
    }
    .sm-icon {
        width: 36px;
        height: 36px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        flex-shrink: 0;
    }
    .sm-name {
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
    }
    .sm-viewer {
        background: rgba(0, 0, 0, 0.6);
    }
    .sm-pdf {
        display: block;
        width: 100%;
        height: 80vh;
        border: 0;
    }
</style>

<div class="content-body fadeIn">
    <div class="mg-b-20 mg-lg-b-25">
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb breadcrumb-style1 mg-b-10">
                <li class="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
                {#if batch}
                    <li class="breadcrumb-item">
                        <a href="/dashboard/studentStudyMaterial" on:click|preventDefault={() => load()}>Study Material</a>
                    </li>
                    {#if path.length}
                        <li class="breadcrumb-item">
                            <a href="/dashboard/studentStudyMaterial" on:click|preventDefault={() => load(batch._id)}>{batch.name}</a>
                        </li>
                        {#each path as aFolder, index}
                            {#if index < path.length - 1}
                                <li class="breadcrumb-item">
                                    <a href="/dashboard/studentStudyMaterial" on:click|preventDefault={() => load(batch._id, aFolder._id)}>{aFolder.name}</a>
                                </li>
                            {:else}
                                <li class="breadcrumb-item active" aria-current="page">{aFolder.name}</li>
                            {/if}
                        {/each}
                    {:else}
                        <li class="breadcrumb-item active" aria-current="page">{batch.name}</li>
                    {/if}
                {:else}
                    <li class="breadcrumb-item active" aria-current="page">Study Material</li>
                {/if}
            </ol>
        </nav>
        <h4 class="mg-b-0">{path.length ? path[path.length - 1].name : batch ? batch.name : "Study Material"}</h4>
        <p class="tx-11 tx-spacing-1 tx-color-03 tx-medium mg-b-5 mg-t-5">Notes, lectures and links shared by your institute</p>
    </div>
    <hr class="full-width">

    {#if isLoading}
        <div class="d-flex justify-content-center pd-y-40">
            <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
        </div>
    {:else if !batch}
        {#if batches.length}
            <div class="row">
                {#each batches as aBatch}
                    <div class="col-sm-6 col-lg-4 mg-b-20">
                        <button type="button" class="card card-body wd-100p sm-row" on:click={() => load(aBatch._id)}>
                            <span class="sm-icon bg-primary-light text-primary"><i data-feather="folder" /></span>
                            <span class="sm-name tx-semibold">{aBatch.name}</span>
                            <i data-feather="chevron-right" class="tx-color-03" />
                        </button>
                    </div>
                {/each}
            </div>
        {:else}
            <div class="d-flex align-items-center justify-content-center" style="flex-direction:column;">
                <div style="width:250px">
                    <img src="/images/empty.svg" class="img-fluid" alt="No batches" />
                    <p class="mg-b-0 tx-color-03" style="text-align:center;">You are not enrolled in any batch yet.</p>
                </div>
            </div>
        {/if}
    {:else}
        {#if folders.length || files.length}
            <div class="list-group">
                {#each folders as aFolder}
                    <button type="button" class="list-group-item list-group-item-action sm-row" on:click={() => load(batch._id, aFolder._id)}>
                        <span class="sm-icon bg-warning-light text-warning"><i data-feather="folder" /></span>
                        <span class="sm-name tx-medium">{aFolder.name}</span>
                        <i data-feather="chevron-right" class="tx-color-03" />
                    </button>
                {/each}
                {#each files as aFile}
                    <div class="list-group-item sm-row">
                        <span class="sm-icon bg-gray-100 text-secondary"><i data-feather={typeInfo(aFile).icon} /></span>
                        <span class="sm-name">
                            <span class="d-block tx-medium">{aFile.name}</span>
                            <span class="tx-12 tx-color-03">
                                {typeInfo(aFile).label}{aFile.createdOn ? " · " + moment(aFile.createdOn).format("DD MMM YYYY") : ""}
                            </span>
                        </span>
                        <button type="button" class="btn btn-sm btn-primary mg-l-10" on:click={() => openFile(aFile)}>
                            {typeInfo(aFile).action}
                        </button>
                    </div>
                {/each}
            </div>
        {:else}
            <div class="d-flex align-items-center justify-content-center" style="flex-direction:column;">
                <div style="width:250px">
                    <img src="/images/empty.svg" class="img-fluid" alt="Empty folder" />
                    <p class="mg-b-0 tx-color-03" style="text-align:center;">No study material here yet.</p>
                </div>
            </div>
        {/if}
    {/if}
</div>

{#if viewer}
    <!-- Escape closes the viewer through the window keydown handler -->
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div class="modal d-block sm-viewer" tabindex="-1" role="dialog" on:click|self={() => (viewer = null)}>
        <div class="modal-dialog modal-dialog-centered {viewer.type == 'pdf' ? 'modal-xl' : 'modal-lg'}" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h6 class="modal-title">{viewer.name}</h6>
                    {#if viewer.type == "pdf"}
                        <!-- Phones often can't show a PDF inside the page, so offer a plain tab too -->
                        <a href={newTabUrl(viewer)} target="_blank" rel="noopener" class="btn btn-xs btn-outline-secondary mg-l-auto mg-r-10">Open in new tab</a>
                    {/if}
                    <button type="button" class="close {viewer.type == 'pdf' ? 'mg-l-0' : ''}" aria-label="Close" on:click={() => (viewer = null)}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body pd-0">
                    {#if viewer.type == "pdf"}
                        <iframe src={viewerSrc(viewer)} title={viewer.name} class="sm-pdf"></iframe>
                    {:else if viewer.type == "youtubeLecture"}
                        <div class="embed-responsive embed-responsive-16by9">
                            <iframe class="embed-responsive-item" title={viewer.name}
                                src="https://www.youtube-nocookie.com/embed/{youtubeId(viewer.youtubeLink)}"
                                allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>
                        </div>
                    {:else if viewer.type == "recordedLecture"}
                        <!-- Recordings are view-only: no download button or right-click save -->
                        <video src={viewer.downloadUrl} class="wd-100p" controls controlsList="nodownload" disablePictureInPicture
                            on:contextmenu|preventDefault>
                            <track kind="captions" />
                        </video>
                    {:else if viewer.type == "image"}
                        <img src={viewer.downloadUrl} alt={viewer.name} class="img-fluid d-block mx-auto" />
                    {/if}
                </div>
            </div>
        </div>
    </div>
{/if}
